import { useId, useState, type FormEvent, type ReactElement } from 'react';

import { ModalShell } from '@/components/ui/ModalShell';
import { useToast } from '@/components/ui/toast';
import { apiErrorReason } from '@/lib/api';

import { codeError, registrationError } from '../presentation';
import { useCorrectCode } from '../use-document-registration';
import { TextField } from '@/components/ui/TextField';

// Corrección del código de un documento reservado (solo Calidad, spec §6.2.4): el
// código anterior queda libre para otro documento.

// Rechazos que se marcan en el campo (como en el alta, QA F1 COD-03).
const CODE_REASONS = new Set(['code_in_use', 'code_retired', 'same_code', 'code_pattern_mismatch']);

export function CorrectCodeModal({
  entryId,
  code,
  onClose,
}: {
  entryId: string;
  code: string;
  onClose: () => void;
}): ReactElement {
  const formId = useId();
  const mutation = useCorrectCode(entryId);
  const { showToast } = useToast();
  const [value, setValue] = useState(code);
  const [checked, setChecked] = useState(false);
  const [codeTaken, setCodeTaken] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const next = value.trim();
  const unchanged = next === code;
  const fieldError = codeTaken ?? (checked ? codeError(value) : null);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setChecked(true);
    if (unchanged || mutation.isPending || codeError(value) !== null) {
      return;
    }
    setFormError(null);
    try {
      const updated = await mutation.mutateAsync(next);
      showToast(`Código ${code} corregido a ${updated.code}; ${code} quedó libre`);
      onClose();
    } catch (err) {
      const reason = apiErrorReason(err);
      if (reason !== undefined && CODE_REASONS.has(reason)) {
        setCodeTaken(registrationError(err));
      } else {
        setFormError(registrationError(err));
      }
    }
  }

  return (
    <ModalShell
      open
      title="Corregir código"
      busy={mutation.isPending}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn mut sm"
            onClick={onClose}
            disabled={mutation.isPending}
          >
            Cancelar
          </button>
          <button
            type="submit"
            form={formId}
            className="btn sm"
            disabled={unchanged || mutation.isPending}
          >
            {mutation.isPending ? 'Corrigiendo…' : 'Corregir código'}
          </button>
        </>
      }
    >
      <p className="modal-body">
        El documento sigue reservado con el código nuevo y <span className="mono">{code}</span>{' '}
        queda libre para otro documento.
      </p>
      <form id={formId} className="form-grid" noValidate onSubmit={(e) => void submit(e)}>
        <TextField
          label="Código nuevo"
          value={value}
          maxLength={60}
          mono
          hint="Si el tipo de documento tiene patrón (proceso y tipo, o SAP), el código nuevo debe seguirlo."
          error={fieldError}
          onChange={(v) => {
            setCodeTaken(null);
            setValue(v);
          }}
        />
        {formError && (
          <p className="fld-error" role="alert">
            {formError}
          </p>
        )}
      </form>
    </ModalShell>
  );
}
